/**
 * Крипто-сигналы: то, чего нет на обычных биржах.
 *
 * Крипторынок живёт вокруг биткойна и стейблкоинов, и у каждой монеты
 * есть спот и фьючерсы с разными участниками. Идеи раздела смотрят за
 * пределы одного графика: на BTC, на индексы TOTAL и USDT.D, на перевес
 * покупок на споте и на контрактах.
 *
 * Индексы сервер копит сам с сентября 2026 года — на более старой
 * истории такие идеи честно молчат, и об этом сказано в паспорте.
 */
import { INDEX_VENUE } from '@specter/types';
import { above, adx, against, breakOut, close, crossAgainst, ema, ind, mirror, number, onMarket, rvol, spec, weight, } from './kit.js';
const spotCvd = ind('spot_cvd', { period: 20 });
const spotDelta = ind('spot_cvd', { period: 20 }, 'delta');
const futuresCvd = ind('futures_cvd', { period: 20 });
const roc20 = ind('roc', { period: 20 });
export const CRYPTO_TEMPLATES = [
    {
        id: 'btc-leads',
        name: 'BTC ведёт',
        icon: '🟠',
        category: 'crypto',
        popular: true,
        hint: 'Вход в альткоин только тогда, когда биткойн сам идёт в ту же сторону.',
        steps: ['BTC на часе по нужную сторону от EMA 50', 'Монета пересекла свою EMA 20', 'Объём'],
        keywords: ['btc', 'биткойн', 'альткоины', 'альты', 'корреляция', 'лидер'],
        passport: {
            level: 'novice',
            market: 'trend',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '1H',
            idea: 'Почти все альткоины ходят за биткойном. Покупать альт разумно, когда BTC растёт, а продавать — когда он падает.',
            why: 'Когда биткойн падает, из альтов уходят первыми — идти против него значит плыть против течения всего рынка.',
            fails: 'В редкие «сезоны альтов», когда альты растут на падающем BTC, фильтр отсекает хорошие входы.',
            tip: 'На самом BTC идея превращается в обычный откат к EMA 20 — её смысл в альтах.',
        },
        build: (o) => spec(o, 'BTC ведёт', mirror((side) => ({
            context: [onMarket(side.beyond(close, ema(50)), 'binance', 'BTC/USDT', '1H')],
            trigger: side.cross(close, ema(20)),
            confirmations: [
                weight('c1', onMarket(side.beyond(ema(20), ema(50)), 'binance', 'BTC/USDT', '1H'), 50),
                weight('c2', above(rvol, number(1.2)), 50),
            ],
        })), 0),
    },
    {
        id: 'stables-out',
        name: 'Деньги выходят из стейблов',
        icon: '💵',
        category: 'crypto',
        hint: 'Доля USDT на рынке падает — деньги идут в риск; вход по ходу монеты.',
        steps: ['Доминирование USDT падает (ниже своей EMA 20 на 4ч)', 'Монета пересекла EMA 50', 'Весь рынок согласен и объём'],
        keywords: ['usdt.d', 'доминирование', 'стейблкоины', 'dominance', 'риск', 'индекс'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'swing',
            style: 'with-trend',
            timeframe: '4H',
            idea: 'USDT.D — доля Tether в рынке. Падает — люди меняют доллары на монеты. Растёт — бегут из монет в доллары.',
            why: 'Доминирование стейблов показывает настроение всего рынка раньше, чем цена отдельной монеты.',
            fails: 'Индекс сервер копит с сентября 2026: на более старой истории сигналов не будет. В новостные дни индекс дёргается.',
            tip: 'Для продаж — наоборот: доминирование USDT растёт, деньги прячутся.',
        },
        build: (o) => spec(o, 'Деньги выходят из стейблов', mirror((side) => ({
            context: [onMarket(against(side)(close, ema(20)), INDEX_VENUE, 'USDT.D', '4H')],
            trigger: side.cross(close, ema(50)),
            confirmations: [
                weight('c1', onMarket(side.beyond(close, ema(20)), INDEX_VENUE, 'TOTAL', '4H'), 50),
                weight('c2', above(rvol, number(1.2)), 50),
            ],
        })), 0),
    },
    {
        id: 'total-up',
        name: 'Растёт весь рынок',
        icon: '🌐',
        category: 'crypto',
        hint: 'Капитализация всего рынка (TOTAL) по нашу сторону, и монета пробивает свой экстремум.',
        steps: ['TOTAL на 4ч по нужную сторону от EMA 20', 'Пробит экстремум 20 баров', 'Объём и сила'],
        keywords: ['total', 'капитализация', 'рынок', 'индекс'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'swing',
            style: 'breakout',
            timeframe: '4H',
            idea: 'Пробой монеты надёжнее, когда растёт весь рынок, а не одна она.',
            why: 'Пробой на общем подъёме подпитывается притоком денег во весь рынок; одиночный пробой быстро выдыхается.',
            fails: 'Индекс сервер копит с сентября 2026 — на старой истории сигналов нет.',
            tip: 'Хорошо сочетается с «BTC ведёт»: общий рынок, лидер и сама монета в одну сторону.',
        },
        build: (o) => spec(o, 'Растёт весь рынок', mirror((side) => ({
            context: [onMarket(side.beyond(close, ema(20)), INDEX_VENUE, 'TOTAL', '4H')],
            trigger: breakOut(side, 20),
            confirmations: [weight('c1', above(rvol, number(1.3)), 60), weight('c2', above(adx, number(20)), 40)],
        })), 0),
    },
    {
        id: 'spot-leads',
        name: 'Спот ведёт цену',
        icon: '🛒',
        category: 'crypto',
        popular: true,
        hint: 'На споте покупают больше, чем продают, и цена пересекает среднюю — рост на настоящих покупках.',
        steps: ['Перевес покупок на споте (CVD) за 20 баров', 'Цена пересекла EMA 20', 'Дельта спота и объём'],
        keywords: ['cvd', 'спот', 'дельта', 'покупки', 'binance'],
        passport: {
            level: 'skilled',
            market: 'any',
            horizon: 'day',
            style: 'with-trend',
            timeframe: '1H',
            idea: 'CVD спота — разница между покупками и продажами «по рынку» на споте Binance. Растёт — монеты реально покупают.',
            why: 'Рост на споте держится на покупке монет, а не на ставках с плечом: такой рост устойчивее.',
            fails: 'Когда цену двигают фьючерсы, спотовый перевес может отставать и давать поздние сигналы.',
            tip: 'Считается по сделкам Binance: на монетах без спота на Binance идея молчит.',
        },
        build: (o) => spec(o, 'Спот ведёт цену', mirror((side) => ({
            context: [side.beyond(spotCvd, number(0))],
            trigger: side.cross(close, ema(20)),
            confirmations: [weight('c1', side.beyond(spotDelta, number(0)), 50), weight('c2', above(rvol, number(1.2)), 50)],
        })), 0),
    },
    {
        id: 'spot-breakout',
        name: 'Пробой, подтверждённый спотом',
        icon: '🛒',
        category: 'crypto',
        hint: 'Пробой экстремума засчитывается, только если на споте в это время покупают (или продают для шорта).',
        steps: ['Перевес на споте в нашу сторону', 'Пробит экстремум 20 баров', 'Объём'],
        keywords: ['cvd', 'спот', 'пробой', 'подтверждение'],
        passport: {
            level: 'skilled',
            market: 'trend',
            horizon: 'day',
            style: 'breakout',
            timeframe: '1H',
            idea: 'Обычный пробой экстремума, но с проверкой: стоят ли за ним покупки монет на споте.',
            why: 'Пробои, сделанные одним плечом на фьючерсах, часто возвращаются; поддержанные спотом — держатся.',
            fails: 'На медленном рынке спотовый перевес колеблется около нуля и не помогает отличить пробой.',
            tip: 'Сравните с «Пробоем экстремума» в студии: видно, сколько ложных пробоев отсекает спот.',
        },
        build: (o) => spec(o, 'Пробой, подтверждённый спотом', mirror((side) => ({
            context: [side.beyond(spotCvd, number(0))],
            trigger: breakOut(side, 20),
            confirmations: [weight('c1', above(rvol, number(1.3)), 60), weight('c2', side.beyond(spotDelta, number(0)), 40)],
        })), 0),
    },
    {
        id: 'spot-divergence',
        name: 'Расхождение цены и спота',
        icon: '🧭',
        category: 'crypto',
        hint: 'Цена идёт в одну сторону, а покупки на споте — в другую: движение без опоры или тихий набор позиции.',
        steps: ['Цена за 20 баров прошла заметный путь', 'Перевес на споте развернулся против неё'],
        keywords: ['cvd', 'спот', 'дивергенция', 'расхождение', 'дельта', 'binance'],
        passport: {
            level: 'pro',
            market: 'any',
            horizon: 'swing',
            style: 'counter-trend',
            timeframe: '4H',
            idea: 'Цена упала, а на споте начали покупать больше, чем продавать, — кто-то тихо набирает. Или наоборот: рост, а монеты продают.',
            why: 'Спот — это настоящие монеты. Когда они уходят против цены, движение держится на плече и легко разворачивается.',
            fails: 'Тихий набор может длиться долго: цена продолжает идти против, пока набор не закончится.',
            tip: 'Считается по сделкам Binance; на монетах без спота на Binance молчит.',
        },
        build: (o) => spec(o, 'Расхождение цены и спота', mirror((side) => ({
            context: [against(side)(roc20, number(-0.5 * side.sign))],
            trigger: side.cross(spotCvd, number(0)),
            confirmations: [
                weight('c1', against(side)(roc20, number(-1.5 * side.sign)), 40),
                weight('c2', side.beyond(spotDelta, number(0)), 30),
                weight('c3', above(rvol, number(1.2)), 30),
            ],
        })), 30),
    },
    {
        id: 'spot-vs-futures',
        name: 'Спот против фьючерсов',
        icon: '⚖️',
        category: 'crypto',
        popular: true,
        hint: 'Цену ведут контракты, а монеты на споте идут в обратную сторону: рост на одних ставках или тихий набор на падении.',
        steps: ['Цена за 20 баров прошла заметный путь', 'На споте перевес против движения цены', 'Контракты развернулись вслед за ценой'],
        keywords: ['cvd', 'спот', 'фьючерсы', 'контракты', 'расхождение', 'binance'],
        passport: {
            level: 'pro',
            market: 'any',
            horizon: 'day',
            style: 'counter-trend',
            timeframe: '1H',
            idea: 'Цена упала, монеты на споте покупают, а на фьючерсах ставят на падение — толпа с плечом против тех, кто покупает монеты.',
            why: 'Плечевые позиции против спотового набора — топливо для резкого движения против толпы, когда их начнут закрывать.',
            fails: 'Если спотовый набор иссякает, фьючерсы оказываются правы и движение продолжается.',
            tip: 'На истории одна из самых точных идей библиотеки — проверьте на своей монете в студии.',
        },
        build: (o) => spec(o, 'Спот против фьючерсов', mirror((side) => ({
            context: [against(side)(roc20, number(-0.5 * side.sign)), side.beyond(spotCvd, number(0))],
            // Контракты разворачиваются вслед за ценой — против спотового перевеса.
            trigger: crossAgainst(side)(futuresCvd, number(0)),
            confirmations: [
                weight('c1', against(side)(roc20, number(-1.5 * side.sign)), 40),
                weight('c2', side.beyond(spotDelta, number(0)), 30),
                weight('c3', above(rvol, number(1.2)), 30),
            ],
        })), 30),
    },
];
//# sourceMappingURL=crypto.js.map